import { useState } from 'react';
import { Package, Plus, Search } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useBrandsQuery, useCategoriesQuery, useCreateProductMutation, useDeleteProductMutation,
  useProductsQuery, useUnitsQuery, type Product,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatMoney, formatQuantity } from '@/lib/format';
import {
  Alert, Badge, Button, Card, Checkbox, ConfirmDialog, EmptyState, ErrorState, Input,
  LoadingState, Modal, PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { UnitPicker } from '@/components/UnitPicker';
import { CategoryPicker } from '@/components/CategoryPicker';
import { BrandPicker } from '@/components/BrandPicker';
import { ProductDetailModal } from './ProductDetailModal';

const STATUS_TONE = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  DISCONTINUED: 'warning',
} as const;

const emptyForm = {
  name: '',
  sku: '',
  baseUnitId: '',
  categoryId: '',
  brandId: '',
  barcode: '',
  costPrice: '',
  sellingPrice: '',
  reorderLevel: '',
  trackInventory: true,
};

export function ProductsPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('');

  const { data, isLoading, isError, error, refetch, isFetching } = useProductsQuery({
    page,
    limit,
    search: search || undefined,
    categoryId: categoryId || undefined,
    status: status || undefined,
  });

  // Reference data for the create form. Units are needed to pick a base unit,
  // which is the one field a product cannot be created without.
  const { data: units } = useUnitsQuery({ limit: 100, isActive: true });
  const { data: categories } = useCategoriesQuery({ limit: 200 });
  const { data: brands } = useBrandsQuery({ limit: 200 });

  const [createProduct, createState] = useCreateProductMutation();
  const [deleteProduct] = useDeleteProductMutation();

  const [creating, setCreating] = useState(false);
  const [openProductId, setOpenProductId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Product | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const fieldErrors = apiFieldErrors(createState.error);
  const baseUnits = units?.items ?? [];

  const submit = async (): Promise<void> => {
    const created = await createProduct({
      name: form.name,
      sku: form.sku,
      baseUnitId: form.baseUnitId,
      categoryId: form.categoryId || undefined,
      brandId: form.brandId || undefined,
      barcode: form.barcode || undefined,
      costPrice: form.costPrice ? Number(form.costPrice) : undefined,
      sellingPrice: form.sellingPrice ? Number(form.sellingPrice) : undefined,
      reorderLevel: form.reorderLevel ? Number(form.reorderLevel) : undefined,
      trackInventory: form.trackInventory,
    })
      .unwrap()
      .catch(() => null);

    if (created) {
      setCreating(false);
      setForm(emptyForm);
      // Straight into the detail view: variants and units are set up there.
      setOpenProductId(created.id);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await deleteProduct(pendingDelete.id).unwrap();
      setPendingDelete(null);
    } catch (deletionError) {
      // The API refuses while stock remains — show exactly why.
      setDeleteError(apiErrorMessage(deletionError, 'This product could not be archived'));
    }
  };

  return (
    <>
      <PageHeader
        title="Products"
        description="What the business buys and sells. Stock is held against a product's variants."
        action={
          <Can permission={P.PRODUCT_CREATE}>
            <Button icon={Plus} onClick={() => setCreating(true)}>New product</Button>
          </Can>
        }
      />

      {baseUnits.length === 0 && !isLoading && (
        <div className="mb-4">
          <Alert tone="info">
            No units of measure yet. Every product needs a base unit — you can create the first one
            while adding a product.
          </Alert>
        </div>
      )}

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Products', value: String(data.summary.count) },
            { label: 'Active', value: String(data.summary.active), hint: 'on sale' },
            {
              label: 'Variants',
              value: String(data.summary.variants),
              hint: 'stock is held against these',
            },
            {
              label: 'Discontinued',
              value: String(data.summary.discontinued),
              hint: `${data.summary.inactive} also inactive`,
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="min-w-56 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Name, SKU or barcode"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="w-full sm:w-56">
            <Select
              label="Category"
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All categories</option>
              {categories?.items.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </Select>
          </div>

          <div className="w-full sm:w-44">
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="DISCONTINUED">Discontinued</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Package}
            title={search || categoryId || status ? 'Nothing matches those filters' : 'No products yet'}
            description={
              search || categoryId || status
                ? 'Try a different search or clear the filters.'
                : 'Add your first product to start tracking stock against it.'
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th>Category</Th>
                  <Th>Base unit</Th>
                  <Th numeric>Cost</Th>
                  <Th numeric>Price</Th>
                  <Th numeric>Reorder at</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((product) => (
                  <tr key={product.id} className="hover:bg-sunken">
                    <Td>
                      <button
                        type="button"
                        className="text-left font-medium hover:underline"
                        onClick={() => setOpenProductId(product.id)}
                      >
                        {product.name}
                      </button>
                      <p className="text-xs text-muted">
                        {product.sku}
                        {product._count && product._count.variants > 1
                          ? ` · ${product._count.variants} variants`
                          : ''}
                      </p>
                    </Td>
                    <Td className="text-secondary">{product.category?.name ?? '—'}</Td>
                    <Td className="text-secondary">{product.baseUnit?.symbol ?? '—'}</Td>
                    <Td numeric className="text-secondary">
                      <Can permission={P.PRODUCT_COST_VIEW} fallback={<span>—</span>}>
                        {formatMoney(product.costPrice, format)}
                      </Can>
                    </Td>
                    <Td numeric>{formatMoney(product.sellingPrice, format)}</Td>
                    <Td numeric className="text-secondary">
                      {product.reorderLevel ? formatQuantity(product.reorderLevel, format) : '—'}
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[product.status]}>
                        {product.status.toLowerCase()}
                      </Badge>
                    </Td>
                    <Td className="text-right">
                      <Can permission={P.PRODUCT_DELETE}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setDeleteError(null);
                            setPendingDelete(product);
                          }}
                        >
                          Archive
                        </Button>
                      </Can>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
                limit={limit}
                onLimitChange={(next) => {
                  setLimit(next);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New product"
        description="A default variant is created automatically — stock is always held against a variant."
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              onClick={() => void submit()}
              loading={createState.isLoading}
              disabled={!form.name || !form.sku || !form.baseUnitId}
            >
              Create product
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {createState.error && <Alert tone="danger">{apiErrorMessage(createState.error)}</Alert>}

          <Input
            label="Name"
            required
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            error={fieldErrors.name}
          />
          <Input
            label="SKU"
            required
            placeholder="BEV-COLA-500"
            value={form.sku}
            onChange={(event) => setForm({ ...form, sku: event.target.value.toUpperCase() })}
            error={fieldErrors.sku}
            hint="Unique across the business."
          />

          <UnitPicker
            label="Base unit"
            required
            value={form.baseUnitId}
            onChange={(baseUnitId) => setForm({ ...form, baseUnitId })}
            units={baseUnits}
            error={fieldErrors.baseUnitId}
            hint="Every stock quantity is stored in this unit. It cannot change once stock moves."
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <CategoryPicker
              value={form.categoryId}
              onChange={(categoryId) => setForm({ ...form, categoryId })}
              categories={categories?.items ?? []}
              error={fieldErrors.categoryId}
            />
            <BrandPicker
              value={form.brandId}
              onChange={(brandId) => setForm({ ...form, brandId })}
              brands={brands?.items ?? []}
              error={fieldErrors.brandId}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Cost price"
              type="number"
              min="0"
              step="0.0001"
              value={form.costPrice}
              onChange={(event) => setForm({ ...form, costPrice: event.target.value })}
              error={fieldErrors.costPrice}
            />
            <Input
              label="Selling price"
              type="number"
              min="0"
              step="0.0001"
              value={form.sellingPrice}
              onChange={(event) => setForm({ ...form, sellingPrice: event.target.value })}
              error={fieldErrors.sellingPrice}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Barcode"
              value={form.barcode}
              onChange={(event) => setForm({ ...form, barcode: event.target.value })}
              error={fieldErrors.barcode}
            />
            <Input
              label="Reorder level"
              type="number"
              min="0"
              step="0.0001"
              value={form.reorderLevel}
              onChange={(event) => setForm({ ...form, reorderLevel: event.target.value })}
              hint="Below this, the product shows as low stock."
            />
          </div>

          <Checkbox
            label="Track inventory"
            description="Turn this off for services and anything that has no stock ledger."
            checked={form.trackInventory}
            onChange={(event) => setForm({ ...form, trackInventory: event.target.checked })}
          />
        </div>
      </Modal>

      {openProductId && (
        <ProductDetailModal productId={openProductId} onClose={() => setOpenProductId(null)} />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Archive this product?"
        message={
          deleteError ??
          `${pendingDelete?.name ?? ''} and its variants will be archived. Ledger history is kept and stays readable.`
        }
        confirmLabel="Archive"
        onConfirm={() => void confirmDelete()}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
      />
    </>
  );
}
